import React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { CommentsCommentFragment } from 'generated/resources/graphql'

import { ReactionsReactionValues } from 'modules/core/components/Reactions/Reaction'
import { MentionedAccess } from 'modules/core/components/TextEditor/plugins/DebouncedMentionsPlugin'
import { useCallback } from 'modules/core/hooks/useCallback'
import { useEffect, useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { Result, UnhandledResult } from 'modules/core/lib/handleMutation'

import CommentEditor, { CommentsCommentValues } from './CommentEditor'
import CommentsList, { CommentsListType } from './CommentsList'
import CommentsTitle from './CommentsTitle'
import './index.styles.css'
interface Props {
  type?: CommentsListType
  previousComments?: CommentsCommentFragment[]
  comments: CommentsCommentFragment[]
  totalCount: number
  onShowMore?: () => void
  onAddComment: (
    variables: CommentsCommentValues
  ) => Promise<UnhandledResult<Result<Omit<unknown, 'errors'>>>>
  resourceId: number
  onFetchCustomMentionableAccesses?: (queryString: string) => Promise<MentionedAccess[]> | undefined
  onAddReaction?: (
    variables: ReactionsReactionValues,
    parentResourceId: number
  ) => Promise<{ id: number } | undefined>
  onDeleteReaction?: (
    id: number,
    parentResourceId: number,
    resourceId: number,
    textCode: string
  ) => Promise<{ success: boolean } | undefined>
  noPadding?: boolean
}

const CommentsContainer = ({
  type = 'fullListWithScroll',
  previousComments,
  comments,
  totalCount,
  onAddComment,
  onShowMore,
  resourceId,
  onFetchCustomMentionableAccesses,
  onAddReaction,
  onDeleteReaction,
  noPadding = false,
}: Props) => {
  const containerRef = React.useRef<HTMLDivElement>(null)
  const editorContainerRef = React.useRef<HTMLDivElement>(null)
  const [isEditorFixed, setIsEditorFixed] = React.useState(false)

  const scrollToBottom = useCallback(() => {
    if (containerRef.current) {
      containerRef.current.scrollTo(0, containerRef.current.scrollHeight)
    }
    handleScroll()
  }, [])

  useUnsafeEffect(() => {
    scrollToBottom()
  }, [comments, scrollToBottom])

  const handleScroll = () => {
    if (editorContainerRef.current && containerRef.current) {
      const editorContainer = editorContainerRef.current.getBoundingClientRect()
      setIsEditorFixed(editorContainer.bottom >= window.innerHeight)
    }
  }

  useEffect(() => {
    handleScroll()
    window.addEventListener('scroll', handleScroll)
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  return (
    <>
      <Box
        paddingX={noPadding ? 's0' : 's24'}
        paddingTop="s24"
        overflowY="scroll"
        ref={containerRef}
        hideScrollBar={!isEditorFixed}
      >
        <CommentsTitle />
        <CommentsList
          type={type}
          previousComments={previousComments ?? []}
          comments={comments}
          totalCount={totalCount}
          onShowMore={onShowMore}
          onAddReaction={onAddReaction}
          onDeleteReaction={onDeleteReaction}
        />
      </Box>
      <Box
        className={'CommentsContainer_textEditorWrapper'}
        paddingY="s16"
        border={{ top: { color: 'grey400', style: 'solid', width: 's1' } }}
        ref={editorContainerRef}
      >
        <CommentEditor
          resourceId={resourceId}
          onAddComment={onAddComment}
          onFocus={scrollToBottom}
          onSubmit={scrollToBottom}
          onFetchCustomMentionableAccesses={onFetchCustomMentionableAccesses}
        />
      </Box>
    </>
  )
}

export default CommentsContainer
